import { useEffect, useMemo, useRef, useState } from "react";
import toast from "../../../utils/toast";
import { apiService } from "../../../services/apiService";
import { exportRowsToExcel } from "../../../utils/excel";
import {
  allocateOvertimeCosts,
  OvertimeCostSegment,
  OvertimeSettings,
} from "../../../utils/overtimeCost";

const LEAVE_HOURS_COLUMN = "İzin-Saat";

export const useReportsTabModel = ({
  mainTab,
  activityDefinitions,
  users,
  projects,
  reports,
  canViewCosts,
  fetchActivities,
  fetchUsers,
  formatCurrencyTr,
  getReportDuration,
  getReportStartEnd,
  getDurationFromUserDurationEntry,
  getWorkedSlicesFromUserDurationEntry,
  resolveApplicableHourlyRate,
  isLeaveActivityReport,
  overtimeSettings,
  t,
}: any) => {
  const [reportMonth, setReportMonth] = useState(new Date().getMonth() + 1);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [monthlyReport, setMonthlyReport] = useState<any[]>([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportActivityIds, setReportActivityIds] = useState<number[]>([]);
  const [reportUserIds, setReportUserIds] = useState<number[]>([]);
  const [reportSearch, setReportSearch] = useState("");
  const [reportActivitySearch, setReportActivitySearch] = useState("");
  const [reportUserSearch, setReportUserSearch] = useState("");
  const [reportProjectNames, setReportProjectNames] = useState<string[]>([]);
  const [reportProjectSearch, setReportProjectSearch] = useState("");
  const [reportView, setReportView] = useState("project");
  const [reportSummaryMode, setReportSummaryMode] = useState("total");
  const [isReportProjectOpen, setIsReportProjectOpen] = useState(false);
  const [isReportActivityOpen, setIsReportActivityOpen] = useState(false);
  const [isReportUserOpen, setIsReportUserOpen] = useState(false);

  const reportActivityRef = useRef<HTMLDivElement | null>(null);
  const reportUserRef = useRef<HTMLDivElement | null>(null);
  const reportProjectRef = useRef<HTMLDivElement | null>(null);

  const reportViewApi = reportView === "leave-hours" ? "user" : reportView;

  const fetchMonthlyReport = async () => {
    try {
      setReportLoading(true);
      const response = await apiService.getMonthlyReportSummary(
        reportMonth,
        reportYear,
        reportViewApi === "activity" ? reportActivityIds : [],
        reportUserIds,
        reportViewApi as "project" | "activity" | "user",
      );
      setMonthlyReport(response);
    } catch (error) {
      toast.error(t("errors:loadFailed"));
    } finally {
      setReportLoading(false);
    }
  };

  useEffect(() => {
    if (mainTab === "reports") {
      void fetchMonthlyReport();
    }
  }, [mainTab, reportMonth, reportYear, reportActivityIds, reportUserIds, reportView]);

  useEffect(() => {
    if (reportView !== "activity") {
      setReportActivityIds([]);
      setReportActivitySearch("");
      setIsReportActivityOpen(false);
    }
    if (reportView !== "project") {
      setReportProjectNames([]);
      setReportProjectSearch("");
      setIsReportProjectOpen(false);
    }
  }, [reportView]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (
        isReportActivityOpen &&
        reportActivityRef.current &&
        target &&
        !reportActivityRef.current.contains(target)
      ) {
        setIsReportActivityOpen(false);
      }
      if (
        isReportUserOpen &&
        reportUserRef.current &&
        target &&
        !reportUserRef.current.contains(target)
      ) {
        setIsReportUserOpen(false);
      }
      if (
        isReportProjectOpen &&
        reportProjectRef.current &&
        target &&
        !reportProjectRef.current.contains(target)
      ) {
        setIsReportProjectOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isReportActivityOpen, isReportProjectOpen, isReportUserOpen]);

  const reportActivityOptions = useMemo(
    () =>
      activityDefinitions
        .filter((activity: any) => activity?.isLeave !== true)
        .map((activity: any) => ({
          id: activity.id,
          label: activity.title,
        })),
    [activityDefinitions],
  );

  const reportUserOptions = useMemo(
    () =>
      users.map((userItem: any) => ({
        id: userItem.id,
        label: `${userItem.firstName} ${userItem.lastName}`.trim(),
      })),
    [users],
  );

  const toggleReportActivityId = (activityId: number) => {
    setReportActivityIds((prev) =>
      prev.includes(activityId)
        ? prev.filter((id: number) => id !== activityId)
        : [...prev, activityId],
    );
  };

  const toggleReportUserId = (userId: number) => {
    setReportUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id: number) => id !== userId)
        : [...prev, userId],
    );
  };

  const toggleReportProjectName = (projectName: string) => {
    setReportProjectNames((prev) =>
      prev.includes(projectName)
        ? prev.filter((name: string) => name !== projectName)
        : [...prev, projectName],
    );
  };

  const filteredMonthlyReport = useMemo(() => {
    if (!reportSearch.trim()) {
      return monthlyReport;
    }
    const query = reportSearch.toLowerCase();
    return monthlyReport.filter((row: any) =>
      String(row.userName || "").toLowerCase().includes(query),
    );
  }, [monthlyReport, reportSearch]);

  const displayMonthlyReport = useMemo(() => {
    const reportMap = new Map<number, any>();
    filteredMonthlyReport.forEach((row: any) => {
      const key = Number(row.userId);
      if (!Number.isFinite(key)) return;
      const matchUser = users.find((user: any) => user.id === key);
      if (!matchUser) return;
      reportMap.set(key, {
        ...row,
        userName: row.userName?.trim()
          ? row.userName
          : `${matchUser.firstName} ${matchUser.lastName}`.trim(),
      });
    });

    const baseUsers =
      reportUserIds.length > 0
        ? users.filter((user: any) => reportUserIds.includes(user.id))
        : users;

    const requiredHoursFallback =
      typeof filteredMonthlyReport[0]?.requiredHours === "number"
        ? filteredMonthlyReport[0].requiredHours
        : typeof monthlyReport[0]?.requiredHours === "number"
          ? monthlyReport[0].requiredHours
          : 0;

    let result = baseUsers.map((user: any) => {
      const existing = reportMap.get(user.id);
      if (existing) return existing;
      return {
        userId: user.id,
        userName: `${user.firstName} ${user.lastName}`.trim(),
        projects: {},
        totalHours: 0,
        leaveHours: 0,
        requiredHours: requiredHoursFallback,
        difference: -requiredHoursFallback,
      };
    });

    if (reportView === "leave-hours") {
      result = result.map((row: any) => {
        const leaveHours = Number(row.leaveHours ?? 0);
        const safeLeaveHours = Number.isFinite(leaveHours) ? leaveHours : 0;
        return {
          ...row,
          projects: { [LEAVE_HOURS_COLUMN]: safeLeaveHours },
          totalHours: safeLeaveHours,
          requiredHours: 0,
          difference: 0,
        };
      });
    }

    if (reportView === "project" && reportProjectNames.length > 0) {
      result = result.map((row: any) => {
        const filteredProjects: Record<string, number> = {};
        reportProjectNames.forEach((name: string) => {
          if (row.projects?.[name] !== undefined) {
            filteredProjects[name] = row.projects[name];
          }
        });
        const totalHours = Object.values(filteredProjects).reduce(
          (sum: number, value: any) => sum + Number(value || 0),
          0,
        );
        const required = Number(row.requiredHours || 0);
        return {
          ...row,
          projects: filteredProjects,
          totalHours,
          difference: totalHours - required,
        };
      });
    }

    if (!reportSearch.trim()) {
      return result;
    }

    const query = reportSearch.toLowerCase();
    return result.filter((row: any) =>
      String(row.userName || "").toLowerCase().includes(query),
    );
  }, [
    filteredMonthlyReport,
    monthlyReport,
    reportProjectNames,
    reportSearch,
    reportUserIds,
    reportView,
    users,
  ]);

  const filteredReportActivities = useMemo(() => {
    const query = reportActivitySearch.toLocaleLowerCase("tr-TR");
    if (!query) return reportActivityOptions;
    return reportActivityOptions.filter((activity: any) =>
      String(activity.label).toLocaleLowerCase("tr-TR").includes(query),
    );
  }, [reportActivityOptions, reportActivitySearch]);

  const filteredReportUsers = useMemo(() => {
    const query = reportUserSearch.toLocaleLowerCase("tr-TR");
    if (!query) return reportUserOptions;
    return reportUserOptions.filter((userItem: any) =>
      String(userItem.label).toLocaleLowerCase("tr-TR").includes(query),
    );
  }, [reportUserOptions, reportUserSearch]);

  const reportGroupColumns = useMemo(() => {
    if (reportView === "user") return [];
    if (reportView === "leave-hours") return [LEAVE_HOURS_COLUMN];

    const allGroupNames = new Set<string>();
    monthlyReport.forEach((userRow: any) => {
      Object.keys(userRow.projects || {}).forEach((p: string) => allGroupNames.add(p));
    });

    if (reportView === "project") {
      projects.forEach((project: any) => {
        const name = project.workflowContainerName || project.name;
        if (name) allGroupNames.add(name);
      });
    }
    if (reportView === "activity") {
      activityDefinitions.forEach((activity: any) => {
        if (activity?.isLeave === true) return;
        if (activity.title) allGroupNames.add(activity.title);
      });
    }

    return Array.from(allGroupNames).sort();
  }, [activityDefinitions, monthlyReport, projects, reportView]);

  const filteredReportProjects = useMemo(() => {
    const query = reportProjectSearch.toLocaleLowerCase("tr-TR");
    if (!query) return reportGroupColumns;
    return reportGroupColumns.filter((name: string) =>
      name.toLocaleLowerCase("tr-TR").includes(query),
    );
  }, [reportGroupColumns, reportProjectSearch]);

  const selectedReportActivityNames = useMemo(
    () =>
      reportActivityOptions
        .filter((option: any) => reportActivityIds.includes(option.id))
        .map((option: any) => option.label),
    [reportActivityIds, reportActivityOptions],
  );

  const visibleReportColumns = useMemo(() => {
    const moveIzinToEnd = (cols: string[]) => {
      const izinCols = cols.filter(
        (name) =>
          name.toLocaleLowerCase("tr-TR").replace(/\s+/g, "") === "izin",
      );
      const rest = cols.filter(
        (name) =>
          name.toLocaleLowerCase("tr-TR").replace(/\s+/g, "") !== "izin",
      );
      return [...rest, ...izinCols];
    };

    if (reportView === "project" && reportProjectNames.length > 0) {
      return moveIzinToEnd(
        reportGroupColumns.filter((name: string) =>
          reportProjectNames.includes(name),
        ),
      );
    }
    if (reportView === "activity" && reportActivityIds.length > 0) {
      return moveIzinToEnd(
        reportGroupColumns.filter((name: string) =>
          selectedReportActivityNames.includes(name),
        ),
      );
    }
    return moveIzinToEnd(reportGroupColumns);
  }, [
    reportActivityIds,
    reportGroupColumns,
    reportProjectNames,
    reportView,
    selectedReportActivityNames,
  ]);

  const reportSummary = useMemo(() => {
    const groupTotals: Record<string, number> = {};
    visibleReportColumns.forEach((name: string) => {
      groupTotals[name] = 0;
    });

    let totalHours = 0;
    let requiredHours = 0;
    let difference = 0;
    let leaveHours = 0;

    displayMonthlyReport.forEach((row: any) => {
      visibleReportColumns.forEach((name: string) => {
        const value = Number(row.projects?.[name] ?? 0);
        if (Number.isFinite(value)) {
          groupTotals[name] += value;
        }
      });

      totalHours += Number(row.totalHours ?? 0) || 0;
      requiredHours += Number(row.requiredHours ?? 0) || 0;
      difference += Number(row.difference ?? 0) || 0;
      leaveHours += Number(row.leaveHours ?? 0) || 0;
    });

    return { groupTotals, totalHours, requiredHours, difference, leaveHours };
  }, [displayMonthlyReport, visibleReportColumns]);

  const reportSummaryDisplay = useMemo(() => {
    const divisor =
      reportSummaryMode === "average" && displayMonthlyReport.length > 0
        ? displayMonthlyReport.length
        : 1;

    const groupValues: Record<string, number> = {};
    visibleReportColumns.forEach((name: string) => {
      groupValues[name] = (reportSummary.groupTotals[name] ?? 0) / divisor;
    });

    return {
      groupValues,
      totalHours: reportSummary.totalHours / divisor,
      requiredHours: reportSummary.requiredHours / divisor,
      difference: reportSummary.difference / divisor,
      leaveHours: reportSummary.leaveHours / divisor,
    };
  }, [displayMonthlyReport.length, reportSummary, reportSummaryMode, visibleReportColumns]);

  const reportMonthlyCostByUserId = useMemo(() => {
    const costs = new Map<number, number>();
    if (!canViewCosts || reportView === "leave-hours") {
      return costs;
    }

    const monthStart = new Date(reportYear, reportMonth - 1, 1, 0, 0, 0, 0);
    const monthEnd = new Date(reportYear, reportMonth, 0, 23, 59, 59, 999);
    const selectedUsers =
      reportUserIds.length > 0 ? new Set(reportUserIds) : null;

    const segments: OvertimeCostSegment[] = [];

    reports.forEach((report: any) => {
      if (isLeaveActivityReport(report)) return;
      const { start, end } = getReportStartEnd(report);
      const fallbackDate = end || start || null;
      if (!fallbackDate || fallbackDate < monthStart || fallbackDate > monthEnd) {
        return;
      }

      let hasUserDuration = false;
      if (report.userDurations) {
        try {
          const parsed = JSON.parse(report.userDurations);
          if (parsed && typeof parsed === "object") {
            Object.entries(parsed).forEach(([rawUserId, entry]) => {
              const userId = Number(rawUserId);
              if (!Number.isFinite(userId)) {
                return;
              }
              if (selectedUsers && !selectedUsers.has(userId)) {
                return;
              }

              const workedSlices = getWorkedSlicesFromUserDurationEntry(entry);
              if (workedSlices.length > 0) {
                hasUserDuration = true;
                workedSlices.forEach((slice: { at: Date; hours: number }) => {
                  if (!Number.isFinite(slice.hours) || slice.hours <= 0) {
                    return;
                  }
                  if (slice.at < monthStart || slice.at > monthEnd) {
                    return;
                  }
                  segments.push({
                    activityId: report.activityId,
                    userId,
                    bucketKey: `user:${userId}`,
                    at: slice.at,
                    hours: slice.hours,
                    rate: resolveApplicableHourlyRate(userId, slice.at),
                  });
                });
                return;
              }

              const duration = getDurationFromUserDurationEntry(entry);
              if (duration <= 0) {
                return;
              }
              hasUserDuration = true;
              const entryDate = fallbackDate;
              if (!entryDate || Number.isNaN(entryDate.getTime())) {
                return;
              }
              if (entryDate < monthStart || entryDate > monthEnd) {
                return;
              }
              segments.push({
                activityId: report.activityId,
                userId,
                bucketKey: `user:${userId}`,
                at: entryDate,
                hours: duration,
                rate: resolveApplicableHourlyRate(userId, entryDate),
              });
            });
          }
        } catch {
          // ignore malformed payload
        }
      }

      if (!hasUserDuration) {
        const assigneeId = report.assigneeId;
        if (!Number.isFinite(assigneeId)) return;
        if (selectedUsers && !selectedUsers.has(assigneeId)) return;

        const duration = getReportDuration(report);
        if (duration <= 0) return;
        segments.push({
          activityId: report.activityId,
          userId: assigneeId,
          bucketKey: `user:${assigneeId}`,
          at: fallbackDate,
          hours: duration,
          rate: resolveApplicableHourlyRate(assigneeId, fallbackDate),
        });
      }
    });

    const allocation = allocateOvertimeCosts({
      segments,
      overtimeSettings: overtimeSettings as OvertimeSettings,
    });
    allocation.costByUserId.forEach((value, userId) => {
      costs.set(userId, value);
    });

    return costs;
  }, [
    canViewCosts,
    getReportDuration,
    getReportStartEnd,
    getDurationFromUserDurationEntry,
    getWorkedSlicesFromUserDurationEntry,
    isLeaveActivityReport,
    overtimeSettings,
    reportMonth,
    reportUserIds,
    reportView,
    reportYear,
    reports,
    resolveApplicableHourlyRate,
  ]);

  const reportCostSummary = useMemo(() => {
    if (!canViewCosts || reportView === "leave-hours") {
      return { total: 0, average: 0 };
    }

    const total = displayMonthlyReport.reduce((sum: number, row: any) => {
      const userId = Number(row.userId);
      return sum + (Number.isFinite(userId) ? reportMonthlyCostByUserId.get(userId) ?? 0 : 0);
    }, 0);

    return {
      total,
      average: displayMonthlyReport.length > 0 ? total / displayMonthlyReport.length : 0,
    };
  }, [canViewCosts, displayMonthlyReport, reportMonthlyCostByUserId, reportView]);

  const reportCostDisplayValue =
    reportSummaryMode === "average"
      ? reportCostSummary.average
      : reportCostSummary.total;

  const getSelectionSummary = (
    options: Array<{ id: number; label: string }>,
    selectedIds: number[],
    emptyLabel: string,
  ) => {
    if (!selectedIds.length) return emptyLabel;
    const labels = options
      .filter((option: any) => selectedIds.includes(option.id))
      .map((option: any) => option.label);
    return `${labels.slice(0, 2).join(", ")}${
      labels.length > 2 ? ` +${labels.length - 2}` : ""
    }`;
  };

  const exportReportToExcel = () => {
    if (!displayMonthlyReport.length) return;

    const rows: Array<Array<string | number>> = [];
    if (reportView === "leave-hours") {
      rows.push(["Personel", "İzin-Saat"]);
      displayMonthlyReport.forEach((row: any) => {
        rows.push([row.userName || "", Number(row.projects?.[LEAVE_HOURS_COLUMN] ?? 0)]);
      });
      exportRowsToExcel({
        fileName: `Aylik_Izin_Saat_${reportMonth}_${reportYear}.xlsx`,
        sheetName: "Izin Saat",
        rows,
      });
      return;
    }

    const headers: Array<string> = ["Personel", ...visibleReportColumns, "TOPLAM", "GEREKEN", "FARK", "İZİN"];
    if (canViewCosts) headers.push("MALİYET (TL)");
    rows.push(headers);

    displayMonthlyReport.forEach((row: any) => {
      const exportRow: Array<string | number> = [row.userName || ""];
      visibleReportColumns.forEach((column: string) => {
        exportRow.push(Number(row.projects?.[column] ?? 0));
      });
      exportRow.push(
        Number(row.totalHours ?? 0),
        Number(row.requiredHours ?? 0),
        Number(row.difference ?? 0),
        Number(row.leaveHours ?? 0),
      );
      if (canViewCosts) {
        exportRow.push(
          reportMonthlyCostByUserId.get(Number(row.userId)) ?? 0,
        );
      }
      rows.push(exportRow);
    });

    exportRowsToExcel({
      fileName: `Aylik_Rapor_${reportMonth}_${reportYear}.xlsx`,
      sheetName: "Rapor",
      rows,
    });
  };

  return {
    reportMonth,
    setReportMonth,
    reportYear,
    setReportYear,
    reportView,
    setReportView,
    reportProjectRef,
    reportProjectNames,
    reportGroupColumns,
    isReportProjectOpen,
    setIsReportProjectOpen,
    setIsReportActivityOpen,
    setIsReportUserOpen,
    reportProjectSearch,
    setReportProjectSearch,
    filteredReportProjects,
    toggleReportProjectName,
    setReportProjectNames,
    reportActivityRef,
    reportActivityOptions,
    fetchActivities,
    isReportActivityOpen,
    reportActivitySearch,
    setReportActivitySearch,
    filteredReportActivities,
    reportActivityIds,
    toggleReportActivityId,
    setReportActivityIds,
    reportUserRef,
    reportUserOptions,
    fetchUsers,
    isReportUserOpen,
    reportUserSearch,
    setReportUserSearch,
    filteredReportUsers,
    reportUserIds,
    toggleReportUserId,
    setReportUserIds,
    getSelectionSummary,
    reportSearch,
    setReportSearch,
    exportReportToExcel,
    visibleReportColumns,
    displayMonthlyReport,
    reportLoading,
    reportMonthlyCostByUserId,
    formatCurrencyTr,
    reportSummaryMode,
    setReportSummaryMode,
    reportSummaryDisplay,
    reportCostDisplayValue,
  };
};
